import { CampaignRecommendationFormState } from '@/client/campaigns';
import { LoadingStatuses } from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { Field, Form, Formik } from 'formik';
import { SelectItemOptionsType } from 'primereact/selectitem';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { number, object } from 'yup';
import { FormikDropdown } from '../../form/FormikDropdown';
import { RECOMMENDATION_CONFIG } from './constants';

const StyledNote = styled.p`
  font-size: var(--xsmall-font-size);
  color: rgba(0, 0, 0, 0.7);
`;

type RecommendationFormProps = {
  isLoading: boolean;
  onSubmit: (values: CampaignRecommendationFormState) => void;
};

export const RecommendationForm: React.FC<RecommendationFormProps> = ({
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const initialValues: CampaignRecommendationFormState = {
    count: undefined,
  };

  // Memoized options to prevent unnecessary re-renders
  const countOptions: SelectItemOptionsType = useMemo(
    () =>
      Array.from({ length: RECOMMENDATION_CONFIG.MAX_COUNT }, (_, i) => ({
        label: String(i + 1),
        value: i + 1,
      })),
    [],
  );

  // Memoized validation schema
  const validationSchema = useMemo(
    () =>
      object<CampaignRecommendationFormState>().shape({
        count: number()
          .required()
          .min(RECOMMENDATION_CONFIG.MIN_COUNT)
          .max(RECOMMENDATION_CONFIG.MAX_COUNT),
      }),
    [t],
  );

  return (
    <Formik<CampaignRecommendationFormState>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {({ isValid }) => {
        return (
          <Form>
            <p>{t('campaign.recommendation.count-description')}</p>
            <FlexContainer justify="flex-start" className="mt-4 mb-4">
              <Field
                id="count"
                name="count"
                component={FormikDropdown}
                options={countOptions}
                placeholder={t('campaign.recommendation.count-placeholder')}
                className="w-4 mr-3"
                aria-label={t('campaign.recommendation.count-label')}
                aria-describedby="count-description"
                showError={false}
              />
              <AppButton
                label={t('campaign.recommendation.generate')}
                state={
                  isLoading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
                isSubmit
                isDisabled={isLoading || !isValid}
                aria-describedby="generation-note"
              />
            </FlexContainer>
            <StyledNote id="generation-note">
              {t('campaign.recommendation.note')}
            </StyledNote>
          </Form>
        );
      }}
    </Formik>
  );
};
